import { useSuspenseQuery } from '@tanstack/react-query';
import { getAuth, getIdToken } from 'firebase/auth';
import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import { Button, LoadingIndicator } from '@gitbook/spine-react';
import { Logger } from '@gitbook/util-debug';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { useHistory } from '../../navigation';
import { apiQuery } from '../../state';

const logger = Logger('ssoProviderLoginList');

/**
 * Lists SSO providers available for login.
 */
export function SSOProviderLoginList(props: {
    organization: api.Organization;
}): React.ReactElement {
    const { organization } = props;
    const history = useHistory();
    const [loading, _, setLoading] = useLoadingState();

    const { data: ssoProviders } = useSuspenseQuery(
        apiQuery('listSSOProviderLoginsInOrganization', { organizationId: organization.id })
    );

    const loginWith = async (provider: api.OrganizationSSOProviderLogin) => {
        setLoading(true);

        analytics.logEvent(
            analytics.createOrgEvent(organization, {
                type: 'auth_sso_sign_in',
            })
        );

        const url = new URL(provider.startURL);

        if (history.location.pathname) {
            url.searchParams.append('location', history.location.pathname);
        }

        const user = getAuth().currentUser;
        logger.info('current user', user);
        const idToken = user ? await getIdToken(user) : null;
        if (idToken) {
            url.searchParams.append('idToken', idToken);
        }

        window.location.href = url.toString();
    };

    const mappedProviders = ssoProviders?.items.map((provider) => (
        <Button
            leadingIcon={loading ? LoadingIndicator : Icon.SsoSaml}
            kind="primary"
            size="large"
            testID="login.ssoButton"
            disabled={loading}
            onPress={() => loginWith(provider)}
            label={`Sign in with ${provider.label}`}
            key={provider.id}
        />
    ));

    return <div className="flex flex-col gap-4">{mappedProviders}</div>;
}
